import Avatar from '@/components/common/Avatar';
import Pagination from '@/components/common/Pagination';
import StatusBadge from '@/components/common/StatusBadge';
import SelectInput from '@/components/Forms/SelectInput';
import TextInput from '@/components/Forms/TextInput';
import ThemeWrapper from '@/components/ThemeWrapper';
import useRoutes from '@/hooks/useRoutes';
import useThemeColors from '@/hooks/useThemeColors';
import StaffLayout from '@/layouts/StaffLayout';
import { router } from '@inertiajs/react';
import clsx from 'clsx';
import {
    ArrowDown,
    ArrowUp,
    ArrowUpDown,
    Edit,
    Eye,
    FileCheck,
    Search,
    X,
} from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';

interface Course {
    id: number;
    title: string;
}

interface Section {
    id: number;
    title: string;
    course_id: number;
    course__title: string;
}

interface Topic {
    id: number;
    title: string;
    section_id: number;
    section__title: string;
    section__course_id: number;
    section__course__title: string;
}

interface Reviewer {
    id: number;
    full_name: string;
    email: string;
}

interface SubmissionData {
    id: number;
    status: 'pending' | 'approved' | 'rejected';
    created_at: string;
    reviewed_at: string | null;
    assignment: {
        id: number;
        title: string;
        course: { id: number; title: string };
        section: { id: number; title: string } | null;
        topic: { id: number; title: string } | null;
    };
    student: {
        id: number;
        full_name: string;
        email: string;
        avatar_url: string | null;
    };
    reviewed_by: {
        id: number;
        full_name: string;
    } | null;
    teacher_comment: string;
    url_1: string | null;
    url_2: string | null;
    evidence_url: string | null;
}

interface Pagination {
    page: number;
    per_page: number;
    total: number;
    num_pages: number;
    has_previous: boolean;
    has_next: boolean;
    previous_page: number | null;
    next_page: number | null;
}

interface AssignmentSubmissionsIndexProps {
    submissions: SubmissionData[];
    courses: Course[];
    sections: Section[];
    topics: Topic[];
    reviewers: Reviewer[];
    pagination: Pagination;
    sort: {
        by: SortKey;
        direction: 'asc' | 'desc';
    };
    filters: {
        search: string;
        course: string;
        section: string;
        topic: string;
        status: string;
        submitted_from: string;
        submitted_to: string;
        reviewed_from: string;
        reviewed_to: string;
        reviewed_by: string;
    };
}

type SortKey =
    | 'student'
    | 'assignment'
    | 'course'
    | 'section'
    | 'status'
    | 'created_at'
    | 'reviewed_at';

const IndexContent: React.FC<AssignmentSubmissionsIndexProps> = ({
    submissions,
    courses,
    sections,
    topics,
    reviewers,
    pagination,
    sort,
    filters,
}) => {
    const { route } = useRoutes();
    const colors = useThemeColors();
    const [searchQuery, setSearchQuery] = useState(filters.search);
    const [localCourse, setLocalCourse] = useState(filters.course);
    const [localSection, setLocalSection] = useState(filters.section);
    const [localTopic, setLocalTopic] = useState(filters.topic);
    const [localStatus, setLocalStatus] = useState(filters.status);
    const [localSubmittedFrom, setLocalSubmittedFrom] = useState(
        filters.submitted_from
    );
    const [localSubmittedTo, setLocalSubmittedTo] = useState(
        filters.submitted_to
    );
    const [localReviewedFrom, setLocalReviewedFrom] = useState(
        filters.reviewed_from
    );
    const [localReviewedTo, setLocalReviewedTo] = useState(filters.reviewed_to);
    const [localReviewedBy, setLocalReviewedBy] = useState(filters.reviewed_by);
    const isFirstRender = useRef(true);
    const skipNextSearchEffect = useRef(false);
    const currentSortBy = sort?.by || 'created_at';
    const currentSortDirection = sort?.direction || 'desc';
    const hasActiveFilters = Boolean(
        filters.search ||
            filters.course ||
            filters.section ||
            filters.topic ||
            filters.status ||
            filters.submitted_from ||
            filters.submitted_to ||
            filters.reviewed_from ||
            filters.reviewed_to ||
            filters.reviewed_by
    );

    // Cascading filter: sections filtered by selected course
    const availableSections = useMemo(() => {
        if (!localCourse) return sections;
        const courseId = Number(localCourse);
        return sections.filter((section) => section.course_id === courseId);
    }, [sections, localCourse]);

    // Cascading filter: topics filtered by selected section or course
    const availableTopics = useMemo(() => {
        if (localSection) {
            const sectionId = Number(localSection);
            return topics.filter((topic) => topic.section_id === sectionId);
        }
        if (localCourse) {
            const courseId = Number(localCourse);
            return topics.filter(
                (topic) => topic.section__course_id === courseId
            );
        }
        return topics;
    }, [topics, localSection, localCourse]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        if (skipNextSearchEffect.current) {
            skipNextSearchEffect.current = false;
            return;
        }
        const timer = setTimeout(() => {
            applyFilters({ search: searchQuery, page: 1 });
        }, 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const applyFilters = (overrides: Record<string, string | number>) => {
        router.get(
            route('staff-assignment-submissions').toString(),
            {
                page: 1,
                sort_by: currentSortBy,
                sort_direction: currentSortDirection,
                search: searchQuery,
                course: filters.course,
                section: filters.section,
                topic: filters.topic,
                status: filters.status,
                submitted_from: filters.submitted_from,
                submitted_to: filters.submitted_to,
                reviewed_from: filters.reviewed_from,
                reviewed_to: filters.reviewed_to,
                reviewed_by: filters.reviewed_by,
                ...overrides,
            },
            { preserveState: true, preserveScroll: true }
        );
    };

    const handleCourseChange = (value: string) => {
        setLocalCourse(value);
        setLocalSection('');
        setLocalTopic('');
        applyFilters({ course: value, section: '', topic: '' });
    };

    const handleSectionChange = (value: string) => {
        setLocalSection(value);
        setLocalTopic('');
        applyFilters({ section: value, topic: '' });
    };

    const handleTopicChange = (value: string) => {
        setLocalTopic(value);
        applyFilters({ topic: value });
    };

    const handleStatusChange = (value: string) => {
        setLocalStatus(value);
        applyFilters({ status: value });
    };

    const handleSubmittedFromChange = (value: string) => {
        setLocalSubmittedFrom(value);
        applyFilters({ submitted_from: value });
    };

    const handleSubmittedToChange = (value: string) => {
        setLocalSubmittedTo(value);
        applyFilters({ submitted_to: value });
    };

    const handleReviewedFromChange = (value: string) => {
        setLocalReviewedFrom(value);
        applyFilters({ reviewed_from: value });
    };

    const handleReviewedToChange = (value: string) => {
        setLocalReviewedTo(value);
        applyFilters({ reviewed_to: value });
    };

    const handleReviewedByChange = (value: string) => {
        setLocalReviewedBy(value);
        applyFilters({ reviewed_by: value });
    };

    const handleSearchClear = () => {
        skipNextSearchEffect.current = true;
        setSearchQuery('');
        applyFilters({ search: '', page: 1 });
    };

    const handleSort = (key: SortKey) => {
        const nextDirection =
            currentSortBy === key
                ? currentSortDirection === 'asc'
                    ? 'desc'
                    : 'asc'
                : key === 'created_at' || key === 'reviewed_at'
                  ? 'desc'
                  : 'asc';

        router.get(
            route('staff-assignment-submissions').toString(),
            {
                page: 1,
                sort_by: key,
                sort_direction: nextDirection,
                search: searchQuery,
                course: filters.course,
                section: filters.section,
                topic: filters.topic,
                status: filters.status,
                submitted_from: filters.submitted_from,
                submitted_to: filters.submitted_to,
                reviewed_from: filters.reviewed_from,
                reviewed_to: filters.reviewed_to,
                reviewed_by: filters.reviewed_by,
            },
            {
                preserveState: true,
                preserveScroll: true,
            }
        );
    };

    const renderSortIcon = (key: SortKey) => {
        if (currentSortBy !== key)
            return (
                <ArrowUpDown
                    size={14}
                    className="text-gray-400 dark:text-neutral-500"
                />
            );
        if (currentSortDirection === 'asc')
            return (
                <ArrowUp
                    size={14}
                    className="text-gray-600 dark:text-neutral-400"
                />
            );
        return (
            <ArrowDown
                size={14}
                className="text-gray-600 dark:text-neutral-400"
            />
        );
    };

    const handlePageChange = (page: number) => {
        router.get(
            route('staff-assignment-submissions').toString(),
            {
                page,
                sort_by: currentSortBy,
                sort_direction: currentSortDirection,
                search: searchQuery,
                course: filters.course,
                section: filters.section,
                topic: filters.topic,
                status: filters.status,
                submitted_from: filters.submitted_from,
                submitted_to: filters.submitted_to,
                reviewed_from: filters.reviewed_from,
                reviewed_to: filters.reviewed_to,
                reviewed_by: filters.reviewed_by,
            },
            {
                preserveState: true,
                preserveScroll: true,
            }
        );
    };

    const getStatusBadge = (status: string) => {
        const map: Record<string, 'approved' | 'rejected' | 'pending'> = {
            approved: 'approved',
            rejected: 'rejected',
        };
        return <StatusBadge status={map[status] ?? 'pending'} />;
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        });
    };

    return (
        <StaffLayout>
            <div className="mx-auto max-w-7xl">
                <div className="mb-8">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                        Entrega de tareas
                    </h1>
                    <p className="mt-2 text-gray-600 dark:text-neutral-300">
                        Gestión de entregas de tareas de estudiantes
                    </p>
                </div>

                {/* Search and Filters */}
                <div className="mb-6 flex flex-col gap-4">
                    {/* Search Bar */}
                    <div className="relative flex-1">
                        <Search
                            className="absolute top-1/2 left-3 -translate-y-1/2 transform text-gray-400 dark:text-neutral-500"
                            size={18}
                        />
                        <input
                            type="text"
                            placeholder="Buscar por nombre o email del estudiante..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={clsx(
                                'w-full rounded-md border bg-white py-2 pr-10 pl-10 text-sm text-gray-900 shadow-sm transition-colors placeholder:text-gray-400 focus:ring-2 focus:outline-none dark:bg-neutral-800 dark:text-white dark:placeholder:text-neutral-500',
                                `border-gray-300 dark:border-neutral-600 ${colors.inputFocus}`
                            )}
                        />
                        {searchQuery && (
                            <button
                                type="button"
                                onClick={handleSearchClear}
                                className="absolute top-1/2 right-3 -translate-y-1/2 transform text-gray-400 hover:text-gray-600 dark:text-neutral-500 dark:hover:text-neutral-300"
                                aria-label="Limpiar búsqueda"
                            >
                                <X size={18} />
                            </button>
                        )}
                    </div>

                    {/* Filters Row */}
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                        {/* Course Filter */}
                        <SelectInput
                            name="course"
                            value={localCourse}
                            onChange={(v) => handleCourseChange(v)}
                        >
                            <option value="">Todos los cursos</option>
                            {courses.map((course) => (
                                <option key={course.id} value={course.id}>
                                    {course.title}
                                </option>
                            ))}
                        </SelectInput>

                        {/* Section Filter */}
                        <SelectInput
                            name="section"
                            value={localSection}
                            onChange={(v) => handleSectionChange(v)}
                            disabled={availableSections.length === 0}
                        >
                            <option value="">Todas las secciones</option>
                            {availableSections.map((section) => (
                                <option key={section.id} value={section.id}>
                                    {section.title}
                                </option>
                            ))}
                        </SelectInput>

                        {/* Topic Filter */}
                        <SelectInput
                            name="topic"
                            value={localTopic}
                            onChange={(v) => handleTopicChange(v)}
                            disabled={availableTopics.length === 0}
                        >
                            <option value="">Todos los temas</option>
                            {availableTopics.map((topic) => (
                                <option key={topic.id} value={topic.id}>
                                    {topic.title}
                                </option>
                            ))}
                        </SelectInput>

                        {/* Status Filter */}
                        <SelectInput
                            name="status"
                            value={localStatus}
                            onChange={(v) => handleStatusChange(v)}
                        >
                            <option value="">Todos los estados</option>
                            <option value="pending">Pendiente</option>
                            <option value="approved">Aprobado</option>
                            <option value="rejected">No aprobado</option>
                        </SelectInput>
                    </div>

                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                        <TextInput
                            label="Envío desde"
                            name="submitted_from"
                            type="date"
                            value={localSubmittedFrom}
                            onChange={(v) => handleSubmittedFromChange(v)}
                        />

                        <TextInput
                            label="Envío hasta"
                            name="submitted_to"
                            type="date"
                            value={localSubmittedTo}
                            onChange={(v) => handleSubmittedToChange(v)}
                        />

                        <TextInput
                            label="Corrección desde"
                            name="reviewed_from"
                            type="date"
                            value={localReviewedFrom}
                            onChange={(v) => handleReviewedFromChange(v)}
                        />

                        <TextInput
                            label="Corrección hasta"
                            name="reviewed_to"
                            type="date"
                            value={localReviewedTo}
                            onChange={(v) => handleReviewedToChange(v)}
                        />

                        <SelectInput
                            label="Profesor revisor"
                            name="reviewed_by"
                            value={localReviewedBy}
                            onChange={(v) => handleReviewedByChange(v)}
                        >
                            <option value="">Todos los profesores</option>
                            {reviewers.map((reviewer) => (
                                <option key={reviewer.id} value={reviewer.id}>
                                    {reviewer.full_name}
                                </option>
                            ))}
                        </SelectInput>
                    </div>
                </div>

                {/* Results Count */}
                {pagination.total > 0 && (
                    <div className="mb-4 text-sm text-gray-600 dark:text-neutral-300">
                        Mostrando {submissions.length} de {pagination.total}{' '}
                        entregas
                    </div>
                )}

                {submissions.length === 0 ? (
                    <div className="rounded-lg bg-white p-12 text-center shadow dark:bg-neutral-800">
                        <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100 dark:bg-neutral-700">
                            {hasActiveFilters ? (
                                <Search
                                    size={32}
                                    className="text-gray-400 dark:text-neutral-500"
                                />
                            ) : (
                                <FileCheck
                                    size={32}
                                    className="text-gray-400 dark:text-neutral-500"
                                />
                            )}
                        </div>
                        <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                            {hasActiveFilters
                                ? 'No se encontraron resultados'
                                : 'No hay entregas de tareas'}
                        </h3>
                        <p className="text-gray-600 dark:text-neutral-300">
                            {hasActiveFilters
                                ? 'Intenta ajustar los filtros de búsqueda.'
                                : 'No hay entregas de tareas en los cursos de tu academia.'}
                        </p>
                    </div>
                ) : (
                    <div className="overflow-hidden rounded-lg bg-white shadow dark:bg-neutral-800">
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 dark:divide-neutral-700">
                                <thead className="bg-gray-50 dark:bg-neutral-700">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-neutral-400">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSort('student')
                                                }
                                                className="inline-flex items-center gap-1"
                                            >
                                                Estudiante
                                                {renderSortIcon('student')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-neutral-400">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSort('assignment')
                                                }
                                                className="inline-flex items-center gap-1"
                                            >
                                                Tarea
                                                {renderSortIcon('assignment')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-neutral-400">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSort('course')
                                                }
                                                className="inline-flex items-center gap-1"
                                            >
                                                Curso
                                                {renderSortIcon('course')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-neutral-400">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSort('section')
                                                }
                                                className="inline-flex items-center gap-1"
                                            >
                                                Sección
                                                {renderSortIcon('section')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-neutral-400">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSort('status')
                                                }
                                                className="inline-flex items-center gap-1"
                                            >
                                                Estado
                                                {renderSortIcon('status')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-neutral-400">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSort('created_at')
                                                }
                                                className="inline-flex items-center gap-1"
                                            >
                                                Entregado
                                                {renderSortIcon('created_at')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-neutral-400">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleSort('reviewed_at')
                                                }
                                                className="inline-flex items-center gap-1"
                                            >
                                                Revisado
                                                {renderSortIcon('reviewed_at')}
                                            </button>
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium tracking-wider text-gray-500 uppercase dark:text-neutral-400">
                                            Acciones
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200 bg-white dark:divide-neutral-700 dark:bg-neutral-800">
                                    {submissions.map((submission) => (
                                        <tr
                                            key={submission.id}
                                            className="hover:bg-gray-50 dark:hover:bg-neutral-700"
                                        >
                                            <td
                                                className="cursor-pointer px-6 py-4 whitespace-nowrap"
                                                onClick={() => {
                                                    window.location.href =
                                                        route(
                                                            'staff-assignment-submissions-detail',
                                                            {
                                                                submission_id:
                                                                    submission.id,
                                                            }
                                                        ).toString();
                                                }}
                                            >
                                                <div className="flex items-center">
                                                    <div className="mr-3">
                                                        <Avatar
                                                            user={
                                                                submission.student
                                                            }
                                                            size="md"
                                                        />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium text-gray-900 hover:text-blue-600 dark:text-white">
                                                            {
                                                                submission
                                                                    .student
                                                                    .full_name
                                                            }
                                                        </div>
                                                        <div className="text-sm text-gray-500 dark:text-neutral-400">
                                                            {
                                                                submission
                                                                    .student
                                                                    .email
                                                            }
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td
                                                className="cursor-pointer px-6 py-4"
                                                onClick={() => {
                                                    window.location.href =
                                                        route(
                                                            'staff-assignment-submissions-detail',
                                                            {
                                                                submission_id:
                                                                    submission.id,
                                                            }
                                                        ).toString();
                                                }}
                                            >
                                                <div className="text-sm text-gray-900 hover:text-blue-600 dark:text-white">
                                                    {
                                                        submission.assignment
                                                            .title
                                                    }
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {
                                                        submission.assignment
                                                            .course.title
                                                    }
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {submission.assignment
                                                        .section?.title || '-'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                {getStatusBadge(
                                                    submission.status
                                                )}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {formatDate(
                                                        submission.created_at
                                                    )}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-gray-900 dark:text-white">
                                                    {submission.reviewed_at
                                                        ? formatDate(
                                                              submission.reviewed_at
                                                          )
                                                        : '-'}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 text-right text-sm font-medium whitespace-nowrap">
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => {
                                                            window.location.href =
                                                                route(
                                                                    'staff-assignment-submissions-detail',
                                                                    {
                                                                        submission_id:
                                                                            submission.id,
                                                                    }
                                                                ).toString();
                                                        }}
                                                        className="text-blue-600 hover:text-blue-900"
                                                        title="Ver detalles"
                                                    >
                                                        <Eye size={18} />
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            window.location.href =
                                                                route(
                                                                    'staff-assignment-submissions-edit',
                                                                    {
                                                                        submission_id:
                                                                            submission.id,
                                                                    }
                                                                ).toString();
                                                        }}
                                                        className="text-gray-600 hover:text-gray-900 dark:text-neutral-400 dark:hover:text-white"
                                                        title="Editar"
                                                    >
                                                        <Edit size={18} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <Pagination
                            currentPage={pagination.page}
                            totalPages={pagination.num_pages}
                            hasPrevious={pagination.has_previous}
                            hasNext={pagination.has_next}
                            onPrevious={() =>
                                handlePageChange(pagination.previous_page!)
                            }
                            onNext={() =>
                                handlePageChange(pagination.next_page!)
                            }
                        />
                    </div>
                )}
            </div>
        </StaffLayout>
    );
};

const Index: React.FC<AssignmentSubmissionsIndexProps> = (props) => {
    return (
        <ThemeWrapper>
            <IndexContent {...props} />
        </ThemeWrapper>
    );
};

export default Index;
